/**
 * Who may open a share code that holds non-shareable MLC content.
 *
 * Activities served by MLC carry a `shareable` flag on their initial JSON:
 * `false` is IDM (deliberately not shareable), `true` is SWP (the shareable
 * variant). Saving such an activity to a share code used to make it reachable
 * by anyone holding the code, which defeats the flag — the code became a way to
 * hand out content the author marked as not shareable.
 *
 * The rule is deliberately a pure function of the saved payload plus the viewer,
 * so the truth table is unit-testable without a store, a network, or a mounted
 * player — the same reasoning as helper/viewEditModeSeed.ts.
 */
/**
 * Does this payload hold content the author marked as not shareable?
 *
 * Only an explicit `false` counts, so a missing flag (a legacy code, or a
 * payload from a source that does not write one) is treated as shareable. That
 * is the deliberate direction to fail in: wrongly restricting an existing code
 * locks users out of their own saved work, while wrongly allowing one leaves
 * the status quo that existed before this rule.
 */
export function isRestrictedContent(savedShareable) {
    return savedShareable === false;
}
/**
 * Is the viewer the owner of this share code?
 *
 * Matches on EITHER the numeric owner id or the saved username, mirroring
 * `checkAuth`'s own ownership test in playerStore.ts. The username fallback is
 * load-bearing, not belt-and-braces: when the backend does not surface a
 * numeric `createdBy` (or returns it under a different key), an id-only check
 * fails and would lock the real owner out of their own activity.
 */
export function isShareCodeOwner(input) {
    var createdBy = input.createdBy, ownerUsername = input.ownerUsername, viewerId = input.viewerId, viewerUsername = input.viewerUsername;
    var ownsById = typeof viewerId === 'number' && typeof createdBy === 'number' && createdBy === viewerId;
    var owner = ownerUsername === null || ownerUsername === void 0 ? void 0 : ownerUsername.trim().toLowerCase();
    var viewer = viewerUsername === null || viewerUsername === void 0 ? void 0 : viewerUsername.trim().toLowerCase();
    var ownsByName = !!owner && !!viewer && owner === viewer;
    return ownsById || ownsByName;
}
/**
 * The decision: should this share code be refused for this viewer?
 *
 * True only when the content is explicitly non-shareable AND the viewer is not
 * its owner. A signed-out viewer can never be an owner, so they are refused —
 * which is the point: the code must not be a way around the flag.
 */
export function isShareCodeRestrictedForViewer(input) {
    return isRestrictedContent(input.savedShareable) && !isShareCodeOwner(input);
}
